import { BREAKPOINTS } from './variables';

// dom helper functions
export const select = (selector: string): Element | null => document.querySelector(selector);
export const selectAll = (selector: string): NodeListOf<Element> => document.querySelectorAll(selector);
export const selectId = (id: string): HTMLElement | null => document.getElementById(id);

// event helper functions
export const on = (
  element: string | Element | NodeListOf<Element> | null, 
  event: string, 
  handler: EventListenerOrEventListenerObject
): void => {
  if (!element || !event || !handler) return;

  element = typeof element === 'string' ? select(element) : element;
  
  if (element instanceof NodeList) {
    element.forEach((el) => el.addEventListener(event, handler));
    console.log('handler added to multiple nodes');
    return;
  }

  if (element instanceof Element) {
    element.addEventListener(event, handler);
  }
};

export const onReady = (fn: () => void): void => {
  if (document.readyState !== 'loading') {
    fn();
  } else {
    document.addEventListener('DOMContentLoaded', fn);
  }
};

export const pick = <T extends object, K extends keyof T>(
  haystack: T, 
  needles: K[]
): Pick<T, K> => {
  return needles.reduce((found, needle) => {
    if (haystack.hasOwnProperty(needle)) {
      found[needle] = haystack[needle];
    }
    return found;
  }, {} as Pick<T, K>);
};

export const init_array = <T>(n: number, fn: (index: number) => T): T[] => 
  Array.from({ length: n }, (_, i) => fn(i));

export const isMobile = (): boolean => window.innerWidth < BREAKPOINTS.mobile;

/**
 * Format a number as currency
 * @param value - The number to format
 * @param locale - The locale to use (default: 'en-US')
 * @param currency - The currency to use (default: 'USD')
 * @returns Formatted currency string
 */
export const formatCurrency = (
  value: number, 
  locale: string = 'en-US', 
  currency: string = 'USD'
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency
  }).format(value);
};

/**
 * Debounce a function call
 * @param func - The function to debounce
 * @param wait - The debounce wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(this: any, ...args: Parameters<T>): void {
    const context = this;
    
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func.apply(context, args);
    }, wait);
  };
};

/**
 * Throttle a function call
 * @param func - The function to throttle
 * @param limit - The throttle limit in milliseconds
 * @returns Throttled function
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean = false;
  
  return function(this: any, ...args: Parameters<T>): void {
    const context = this;
    
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

/**
 * Get a cookie value by name
 * @param name - The cookie name
 * @returns The cookie value or empty string if not found
 */
export const getCookie = (name: string): string => {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }
  return '';
};

/**
 * Set a cookie
 * @param name - The cookie name
 * @param value - The cookie value
 * @param days - Number of days until the cookie expires
 */
export const setCookie = (name: string, value: string, days: number = 30): void => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

/**
 * Generate a random string
 * @param length - The length of the string
 * @returns Random string
 */
export const randomString = (length: number = 8): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};